import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { getAllPosts, getFeaturedPost, BlogPost } from '@/content/blog/posts';
import { PamColumnByline } from '@/components/blog/PamByline';
import DiagnosticCTA from '@/components/blog/DiagnosticCTA';

const formatDate = (dateString: string): string => {
  // Append T00:00:00 to treat YYYY-MM-DD as local time, not UTC
  // This prevents dates from appearing as the previous day in US timezones
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const PostMeta: React.FC<{ post: BlogPost; className?: string }> = ({ post, className = '' }) => (
  <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500 ${className}`}>
    <span className="flex items-center gap-1.5">
      <Calendar className="w-4 h-4" />
      {formatDate(post.publishedAt)}
    </span>
    <span className="flex items-center gap-1.5">
      <Clock className="w-4 h-4" />
      {post.readTime} min read
    </span>
  </div>
);

/** The lead story — Pam's latest letter, set big with a Fraunces headline. */
const FeaturedPost: React.FC<{ post: BlogPost }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group block bg-white rounded-2xl border-2 border-neutral-200 shadow-ledge-lg p-6 sm:p-10 transition-colors hover:border-primary-300"
  >
    <p className="eyebrow mb-4 text-primary-600">Pam's latest letter</p>
    <h2 className="font-display font-bold tracking-tight text-neutral-900 text-3xl sm:text-4xl leading-tight mb-4 group-hover:text-primary-700 transition-colors">
      {post.title}
    </h2>
    <p className="body-large text-neutral-600 mb-6 max-w-2xl">
      {post.excerpt}
    </p>
    <PostMeta post={post} className="mb-6" />
    <span className="inline-flex items-center gap-2 font-display font-semibold text-primary-700 group-hover:gap-3 transition-all">
      Read the letter
      <ArrowRight className="w-5 h-5" />
    </span>
  </Link>
);

/** A warm ledge card for the rest of the column. */
const PostCard: React.FC<{ post: BlogPost }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group flex flex-col bg-white rounded-2xl border-2 border-neutral-200 shadow-ledge p-6 transition-colors hover:border-primary-300"
  >
    <h3 className="font-display font-semibold text-xl text-neutral-900 leading-snug mb-3 group-hover:text-primary-700 transition-colors">
      {post.title}
    </h3>
    <p className="text-base text-neutral-600 leading-relaxed mb-5 flex-1">
      {post.excerpt}
    </p>
    <PostMeta post={post} />
  </Link>
);

/** Letters shown per page — grows by this much on each "More letters". */
const PAGE_SIZE = 6;

const Blog: React.FC = () => {
  const featuredPost = getFeaturedPost();
  const allPosts = getAllPosts();
  const regularPosts = allPosts.filter(post => post.slug !== featuredPost?.slug);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visiblePosts = regularPosts.slice(0, visibleCount);

  return (
    <>
      <Helmet>
        <title>Pam's Column | ASWB Exam Prep, Straight From the LCSW Who Reviewed Every Question</title>
        <meta
          name="description"
          content="Letters from Pamela Coin, LCSW, a Board Approved Clinical Supervisor, on passing the ASWB exam, closing the pass-rate gap, and studying smarter. She reviewed every question herself."
        />
        <meta property="og:title" content="Pam's Column | Social Work Test Prep Academy" />
        <meta property="og:description" content="Letters from Pamela Coin, LCSW on passing the ASWB exam and studying smarter." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/blog" />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />
        <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/blog-og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/blog-og-image.png" />
        <meta name="twitter:title" content="Pam's Column | Social Work Test Prep Academy" />
        <meta name="twitter:description" content="Letters from Pamela Coin, LCSW on passing the ASWB exam and studying smarter." />
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/blog" />
      </Helmet>

      <div className="min-h-screen bg-neutral-50">
        {/* Masthead — this is a column, and here is who writes it */}
        <header className="border-b-2 border-neutral-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center">
            <p className="eyebrow mb-4 text-primary-600">Notes from Pam</p>
            <h1 className="font-display font-bold tracking-tight text-neutral-900 text-4xl md:text-5xl mb-5">
              Pam's Column
            </h1>
            <p className="body-large text-neutral-600 mb-10 max-w-xl mx-auto">
              Honest letters on passing the ASWB, from a licensed clinical social worker
              and Board Approved Clinical Supervisor who has sat on hiring panels and
              watched brilliant social workers get stopped at this one exam. Let's change that.
            </p>
            <div className="flex justify-center">
              <PamColumnByline />
            </div>
          </div>
        </header>

        {/* The column */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16 space-y-12">
          {featuredPost && <FeaturedPost post={featuredPost} />}

          {regularPosts.length > 0 && (
            <section>
              <h2 className="font-display font-bold text-2xl text-neutral-900 mb-6">
                More from the column
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {visiblePosts.map((post) => (
                  <PostCard key={post.slug} post={post} />
                ))}
              </div>
              {visibleCount < regularPosts.length && (
                <div className="flex justify-center mt-10">
                  <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 font-display font-semibold text-primary-700 border-2 border-neutral-200 rounded-2xl px-6 py-3 bg-white shadow-ledge hover:border-primary-300 transition-colors"
                  >
                    More letters
                    <ArrowRight className="w-5 h-5 rotate-90" />
                  </button>
                </div>
              )}
            </section>
          )}

          {/* One warm invitation — no billboard */}
          <DiagnosticCTA campaign="blog_index" />
        </div>
      </div>
    </>
  );
};

export default Blog;
